//src/components/chat/ConversationItem.js
import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Bot, User } from 'lucide-react';

const ConversationItem = ({ conversation, isActive, onClick }) => {
    const getInitials = (name) => {
        if (!name) return '#';
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    // Check if this conversation has a session mode (bot or human)
    const hasSessionMode = conversation.session && conversation.session.mode;
    const isBotMode = hasSessionMode && conversation.session.mode === 'bot';

    const formatLastMessage = (lastMessage) => {
        if (!lastMessage) return 'No messages yet';

        switch (lastMessage.type) {
            case 'image_message':
                return lastMessage.content?.caption ? `📷 ${lastMessage.content.caption}` : '📷 Photo';
            case 'video_message':
                return lastMessage.content?.caption ? `🎥 ${lastMessage.content.caption}` : '🎥 Video';
            case 'audio_message':
                return '🎵 Audio';
            case 'document_message':
                return '📄 Document';
            case 'template_message':
                return `📋 Template: ${lastMessage.content?.template?.name || ''}`;
            case 'text_message':
                return lastMessage.content?.text || 'Text message';
            default:
                return 'New message';
        }
    };

    const getMessageTime = (lastMessage) => {
        if (!lastMessage || !lastMessage.timestamp) return null;
        return formatDistanceToNow(new Date(lastMessage.timestamp), { addSuffix: true });
    };

    // Handle message status indicator
    const getStatusIndicator = (lastMessage) => {
        if (!lastMessage || lastMessage.direction !== 'outgoing') return null;

        switch (lastMessage.status) {
            case 'sent':
                return '✓';
            case 'delivered':
                return '✓✓';
            case 'read':
                return '✓✓';  // Could use styling to color this blue in real app
            case 'failed':
                return '❌';
            default:
                return '⏱️';
        }
    };

    return (
        <div
            onClick={onClick}
            className={`
        px-4 py-3 border-b cursor-pointer transition-colors
        ${isActive ? 'bg-indigo-50' : 'hover:bg-gray-50'}
        ${hasSessionMode && isBotMode ? 'border-l-4 border-l-emerald-500' : ''}
      `}
        >
            <div className="flex items-center space-x-3">
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                    <div className={`
            w-12 h-12 rounded-full flex items-center justify-center
            ${isActive ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-600'}
            font-semibold text-sm
          `}>
                        {getInitials(conversation.name)}
                    </div>

                    {/* Unread count badge */}
                    {conversation.unreadCount > 0 && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center">
                            <span className="text-xs text-white">{conversation.unreadCount}</span>
                        </div>
                    )}

                    {/* Session mode indicator */}
                    {hasSessionMode && (
                        <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center
                            ${isBotMode ? 'bg-emerald-500' : 'bg-indigo-500'}`}>
                            {isBotMode ? (
                                <Bot className="w-3.5 h-3.5 text-white" />
                            ) : (
                                <User className="w-3.5 h-3.5 text-white" />
                            )}
                        </div>
                    )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline">
                        <div className="flex items-center">
                            <h3 className="text-sm font-semibold text-gray-900 truncate">
                                {conversation.name === "undefined" ? conversation.phone : (conversation.name || conversation.phone)}
                            </h3>
                        </div>
                        {conversation.lastMessage && (
                            <span className="text-xs text-gray-500">
                                {getMessageTime(conversation.lastMessage)}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center text-sm text-gray-500">
                        {conversation.lastMessage?.direction === 'outgoing' && (
                            <span className={`mr-1 ${conversation.lastMessage.status === 'read' ? 'text-blue-500' :
                                conversation.lastMessage.status === 'failed' ? 'text-red-500' : 'text-gray-400'}`}>
                                {getStatusIndicator(conversation.lastMessage)}
                            </span>
                        )}
                        <p className="truncate">
                            {formatLastMessage(conversation.lastMessage)}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConversationItem;